/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable import/prefer-default-export */
import * as React from "react";
import classNames from "classnames";

type VariantProps =
  | { variant?: "horizontal"; labelBorder?: boolean }
  | { variant?: "vertical"; labelBorder?: never };

interface Props {
  /** Optional label to show above the field */
  label?: string;
  /** If using a label, the for attribute to bind the label to the field. This must match the id of the passed field. */
  labelFor?: string;
  /** The error message to show */
  error?: string;
  /** Alternative variants that render field differently */
  variant?: "vertical" | "horizontal";
  /** Whether the label should render a bottom border, only on horizontal variant */
  labelBorder?: boolean;
  /** Classes to add to the label */
  labelClasses?: string;
}

type FormFieldProps = Props & VariantProps;

/**
 * A component used to provide form functionality to passed fields, including labels, errors, and hints.
 */
export function FormField({
  label,
  error,
  labelFor,
  labelClasses,
  children,
  variant = "vertical",
  labelBorder,
}: React.PropsWithChildren<FormFieldProps>) {
  const [active, setActive] = React.useState(false);

  return (
    <label
      onMouseDown={() => setActive(true)}
      onBlur={() => setActive(false)}
      htmlFor={labelFor}
      aria-describedby="errorField"
      className={classNames("relative", {
        "flex flex-row": variant === "horizontal",
      })}
    >
      <div
        data-testid="animated-label"
        className={classNames("flex items-center transition-all", {
          "w-0": variant === "horizontal" && active,
          "w-full": variant === "horizontal" && !active,
          "border-b-2": labelBorder && variant === "horizontal",
          [`${labelClasses}`]: labelClasses,
        })}
      >
        {label}
      </div>
      <div className="w-full">{children}</div>
      {error && (
        <div
          id="errorField"
          className={classNames("text-xs text-blush min-h-[36px]", {
            "absolute right-0 bottom-0 z-10 py-5": variant === "horizontal",
            "py-2": variant !== "horizontal",
          })}
        >
          {error}
        </div>
      )}
    </label>
  );
}
